import React, { useCallback, useRef, useState } from "react";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { useGetRfiQuestionnaireSnapshot } from "../../../custom-hooks/queries";
import { getLocationFromProvider } from "../../../utils/addressUtils";
import { formatVqqName } from "../../../utils/vqqNameFormat";
import VqqStatsBar from "./VqqStatsBar";
import VqqSectionSidebar from "./VqqSectionSidebar";
import VqqSectionView from "./VqqSectionView";

const EMPTY_SECTIONS = [];

const buildVendorLine = (rfisDetails) => {
  const location = getLocationFromProvider({
    city: rfisDetails?.location_city,
    state: rfisDetails?.location_state,
    country: rfisDetails?.location_country,
  });
  return [rfisDetails?.vendorName, location].filter(Boolean).join(" · ");
};

const ProgressBar = ({ percent }) => {
  const safe = Math.max(0, Math.min(100, Math.round(percent ?? 0)));
  return (
    <div className="flex items-center gap-2 w-48">
      <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
        <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${safe}%` }} />
      </div>
      <span className="text-xs font-medium text-gray-700">{safe}%</span>
    </div>
  );
};

const VqqResponsesReadOnly = ({ questionnaireId, rfisDetails }) => {
  const { data, isLoading, isError, error, refetch } = useGetRfiQuestionnaireSnapshot(
    questionnaireId
  );

  const sections = data?.sections ?? EMPTY_SECTIONS;
  const [openSet, setOpenSet] = useState(() => new Set());
  const [activeSection, setActiveSection] = useState(null);
  const sectionRefs = useRef(new Map());

  const registerRef = useCallback((sectionId, node) => {
    if (node) sectionRefs.current.set(sectionId, node);
    else sectionRefs.current.delete(sectionId);
  }, []);

  const handleToggle = useCallback((sectionId) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
    setActiveSection(sectionId);
  }, []);

  const handleSidebarSelect = useCallback((sectionId) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      next.add(sectionId);
      return next;
    });
    setActiveSection(sectionId);
    const node = sectionRefs.current.get(sectionId);
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const total = data?.stats?.total_questions ?? 0;
  const complete = data?.stats?.complete_count ?? 0;
  const percent = total > 0 ? (complete / total) * 100 : 0;
  const vendorLine = buildVendorLine(rfisDetails);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        Loading questionnaire&hellip;
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-4 text-sm text-red-700 mx-4 my-4">
        <p className="font-medium">Unable to load the questionnaire snapshot.</p>
        <p className="mt-1 text-red-600">
          {error?.response?.data?.message || error?.message || "Please try again."}
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-3 inline-flex items-center px-3 py-1.5 rounded border border-red-300 bg-white text-red-700 hover:bg-red-100 text-xs font-medium"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="px-4 py-4 space-y-4">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-indigo-50 border border-indigo-100">
            <DocumentTextIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 truncate">
              {formatVqqName(data.name || rfisDetails?.name) || "Questionnaire"}
            </h2>
            {vendorLine ? (
              <p className="text-sm text-gray-500 truncate">{vendorLine}</p>
            ) : null}
          </div>
        </div>
        <ProgressBar percent={percent} />
      </header>

      <VqqStatsBar
        totalQuestions={total}
        approvedCount={data.stats?.approved_count ?? 0}
        completeCount={complete}
        approvedAt={data.approved_at}
        completedAt={data.release_date}
      />

      <div className="flex gap-6 items-start pt-2">
        <VqqSectionSidebar
          sections={sections}
          activeSection={activeSection}
          onSelect={handleSidebarSelect}
        />
        <div className="flex-1 min-w-0 space-y-6">
          <VqqSectionView
            sections={sections}
            openSet={openSet}
            onToggle={handleToggle}
            registerRef={registerRef}
          />
        </div>
      </div>
    </div>
  );
};

export default VqqResponsesReadOnly;
